import { useState, useRef } from 'react';
import { ApiError, api, type PortfolioTenant, type Tenant } from './api.ts';
import { CONNECTION } from './ClientActions.tsx';
import type { AwaitingRow } from './clientViews.ts';
import { copy, copyOutcomeText } from './clipboard.ts';
import { message, plural } from './format.ts';
import { NO_LINK } from './OnboardingGrant.tsx';

/**
 * One client awaiting consent, as the Awaiting consent tab works it.
 *
 * Moved here whole from the Onboarding screen, which this tab replaced. The
 * screen is gone; the job is not, and the job is bookkeeping rather than
 * authorisation — every customer tenant needs its own Global Administrator to
 * click a link, and no amount of product can bulk-authorise that. So what a row
 * offers is the link, one press that copies it and records that it went out,
 * and somewhere to write down what happened.
 *
 * Beside `AwaitingConsent.tsx` rather than inside it for the same reason
 * `ClientRow.tsx` sits beside `Portfolio.tsx`: that file is the tab — what is
 * shown and which rows — and this one is a row, which is a separate question
 * with a long answer.
 */

/** Days waited, in the words of somebody reporting it rather than a duration. */
const ago = (count: number): string =>
  count <= 0 ? 'today' : count === 1 ? '1 day ago' : `${count} days ago`;

/**
 * Which of the three groups a row is in, which decides its words and answers
 * the tab's filter. The split that matters is the one inside `pending_consent`:
 * the connection state is identical whether or not anybody has been asked, and
 * whose court the ball is in is the whole question.
 */
export type ChaseKind = 'not-sent' | 'waiting' | 'attention';

export const chaseKind = (tenant: Tenant): ChaseKind => {
  if (tenant.connectionState !== 'pending_consent') return 'attention';
  return tenant.invitedAt === null ? 'not-sent' : 'waiting';
};

/**
 * The draft email, addressed and worded, for the operator to send themselves.
 *
 * The link is passed in rather than read off the client, because a row the
 * chase payload omitted still has one — it is fetched rather than delivered,
 * and until it has been there is nothing to put in the body.
 */
const mailtoFor = (tenant: PortfolioTenant, consentUrl: string | null): string => {
  const subject = `Granting DriftWatch read-only access for ${tenant.displayName}`;
  const body = [
    `Hello${tenant.contactName ? ` ${tenant.contactName}` : ''},`,
    '',
    'Please ask a Global Administrator of your Microsoft 365 tenant to open the link below and grant consent. It gives read-only access to your Entra configuration so we can assess it against your compliance controls.',
    '',
    consentUrl,
  ].join('\n');

  // Percent-encode the address so a stray ? or & in it cannot append headers to
  // the mailto, then put the @ back: some mail clients will not accept %40.
  const to = encodeURIComponent(tenant.contactEmail ?? '').replace(/%40/g, '@');

  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

interface ChaseRowProps {
  readonly row: AwaitingRow;
  /**
   * Whether this deployment has an Entra application at all. Without one there
   * is no consent link to fetch and `NO_LINK` is the honest answer immediately,
   * rather than after a request that can only come back 503.
   */
  readonly entraConfigured: boolean;
  readonly onPatch: (id: string, patch: Partial<Tenant>) => void;
  readonly onStateChanged: () => void;
  readonly announce: (text: string) => void;
}

export const ChaseRow = ({
  row,
  entraConfigured,
  onPatch,
  onStateChanged,
  announce,
}: ChaseRowProps) => {
  const { tenant, chase, daysWaiting } = row;
  const [note, setNote] = useState(tenant.onboardingNote ?? '');
  const [busy, setBusy] = useState(false);
  /**
   * Every outcome this row produces, rendered underneath it.
   *
   * Never the page-level banner. `AddClient.tsx` and `ImportClients.tsx` both
   * carry the same note and for the same reason: a message about a button that
   * appears several hundred pixels above it reads as nothing having happened.
   * Here it is worse than there — sixty clients means sixty buttons, and a
   * banner could not say which one it was answering.
   */
  const [status, setStatus] = useState<string | null>(null);
  const linkRef = useRef<HTMLElement>(null);

  /**
   * The consent link: known, known to be unavailable, or not yet asked for.
   *
   * `undefined` is the third state and it is not a nicety. A row the chase
   * payload carries has its link already, and a null there means this
   * deployment has no Entra application registered — a fact, and `NO_LINK` says
   * so. A row the payload left out has been told nothing either way, and
   * guessing null for it would put that same sentence on a client whose link
   * exists and works.
   */
  const [link, setLink] = useState<string | null | undefined>(
    chase !== null ? chase.consentUrl : entraConfigured ? undefined : null,
  );

  const ensureLink = async (): Promise<string | null> => {
    if (link !== undefined) return link;
    const { consentUrl } = await api.consentUrl(tenant.id);
    setLink(consentUrl);
    return consentUrl;
  };

  /** Fetch it and show it, without copying or recording anything. */
  const revealLink = async () => {
    try {
      await ensureLink();
    } catch (cause) {
      setStatus(message(cause));
      announce(message(cause));
    }
  };

  const kind = chaseKind(tenant);
  const sent = tenant.invitedAt !== null;
  const connection = CONNECTION[tenant.connectionState];

  const noLink = () => {
    setStatus(NO_LINK);
    announce(NO_LINK);
  };

  const copyOnly = async () => {
    try {
      const url = await ensureLink();
      if (url === null) return noLink();
      const outcome = await copy(url, linkRef.current);
      const text = copyOutcomeText(outcome, `Consent link for ${tenant.displayName}`);
      setStatus(text);
      announce(text);
    } catch (cause) {
      setStatus(message(cause));
      announce(message(cause));
    }
  };

  /**
   * Copy and record in one press. Separating them would be more honest about the
   * two things happening, and would also mean eighty clicks for forty clients —
   * the reason the record is ever kept up to date is that keeping it costs
   * nothing extra.
   */
  const copyAndRecord = async () => {
    setBusy(true);
    try {
      const url = await ensureLink();
      if (url === null) {
        noLink();
        return;
      }
      const outcome = await copy(url, linkRef.current);
      const copied = copyOutcomeText(outcome, `Consent link for ${tenant.displayName}`);

      if (sent) {
        const { reminderCount } = await api.remind(tenant.id);
        onPatch(tenant.id, { reminderCount, lastRemindedAt: new Date().toISOString() });
        setStatus(`${copied} Reminder ${reminderCount} recorded.`);
        announce(`${copied} That is reminder ${reminderCount} for ${tenant.displayName}.`);
      } else {
        await api.markInvited(tenant.id);
        onPatch(tenant.id, { invitedAt: new Date().toISOString() });
        setStatus(`${copied} Recorded as sent.`);
        announce(`${copied} ${tenant.displayName} recorded as sent.`);
      }
      onStateChanged();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        // The customer consented between this list loading and the button being
        // pressed. The row is stale, not the press wrong.
        setStatus(`${tenant.displayName} has since connected — no reminder recorded.`);
        announce(`${tenant.displayName} has since connected. No reminder was recorded.`);
        onStateChanged();
      } else {
        setStatus(message(cause));
        announce(message(cause));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    const next = note.trim();
    if (next === (tenant.onboardingNote ?? '')) return;

    try {
      await api.putOnboardingNote(tenant.id, next || null);
      onPatch(tenant.id, { onboardingNote: next || null });
      setStatus('Note saved.');
      announce(`Note saved for ${tenant.displayName}.`);
    } catch (cause) {
      setStatus(message(cause));
      announce(message(cause));
    }
  };

  return (
    <tbody className="ob-group">
      <tr>
        <td>
          <span className="ob-name">{tenant.displayName}</span>
          <span className="mono note ob-tenant-id">{tenant.entraTenantId}</span>
        </td>
        <td>
          {tenant.contactName || tenant.contactEmail ? (
            <>
              {tenant.contactName && <span className="ob-contact">{tenant.contactName}</span>}
              {tenant.contactEmail && (
                <a className="note ob-contact" href={mailtoFor(tenant, link ?? null)}>
                  {tenant.contactEmail}
                </a>
              )}
            </>
          ) : (
            <span className="note">No contact recorded</span>
          )}
        </td>
        <td>
          {/* Not sent and awaiting consent are the same connection state; what
              differs is whose court the ball is in, so the chip is the same and
              the words are not. */}
          {kind === 'not-sent' ? (
            <>
              <span className="conn conn-pending">Not sent</span>
              {daysWaiting !== null && (
                <span className="note ob-since">Added {ago(daysWaiting)}</span>
              )}
            </>
          ) : kind === 'waiting' ? (
            <>
              <span className="conn conn-pending">Awaiting consent</span>
              {daysWaiting !== null && (
                <span className="note ob-since">Link sent {ago(daysWaiting)}</span>
              )}
            </>
          ) : (
            <>
              <span className={`conn ${connection.chip}`}>{connection.text}</span>
              {tenant.connectionDetail && (
                <span className="note ob-since">{tenant.connectionDetail}</span>
              )}
            </>
          )}
        </td>
        <td>
          {tenant.reminderCount === 0 ? (
            <span className="note">None</span>
          ) : (
            <>
              <span className="num">{plural(tenant.reminderCount, 'reminder')}</span>
              {tenant.lastRemindedAt && (
                <span className="note ob-since">
                  Last {new Date(tenant.lastRemindedAt).toLocaleDateString('en-GB')}
                </span>
              )}
            </>
          )}
        </td>
        <td>
          <div className="toolbar ob-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              aria-busy={busy || undefined}
              onClick={() => void copyAndRecord()}
              disabled={busy}
            >
              {sent ? 'Copy link & mark reminded' : 'Copy link & mark sent'}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={busy}
              onClick={() => void copyOnly()}
            >
              Copy only
            </button>
          </div>
        </td>
      </tr>
      <tr className="ob-detail">
        <td colSpan={5}>
          <div className="ob-detail-grid">
            <div className="ob-linkfield">
              <span className="label" id={`link-${tenant.id}`}>
                Consent link
              </span>
              {/* Never truncated and never one scrolling line: an engineer has to
                  be able to read the whole URL and quote it verbatim, so it wraps
                  and a single click selects all of it.

                  A row the chase payload omitted has no link to show until it is
                  asked for, and asking for every one of them on load would be a
                  request per client for something most rows will never be read
                  from. The buttons above fetch it themselves, so this is for
                  reading rather than for working. */}
              {link === undefined ? (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm ob-reveal"
                  disabled={busy}
                  onClick={() => void revealLink()}
                >
                  Show the consent link
                </button>
              ) : (
                <code ref={linkRef} className="mono ob-link" aria-labelledby={`link-${tenant.id}`}>
                  {link ?? NO_LINK}
                </code>
              )}
            </div>
            <div className="field ob-notefield">
              <label htmlFor={`note-${tenant.id}`}>Note</label>
              <input
                id={`note-${tenant.id}`}
                value={note}
                placeholder="Chased via their service desk; waiting on their IT partner…"
                onChange={(event) => setNote(event.target.value)}
                onBlur={() => void saveNote()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
              />
              <span className="hint">Saved when you leave the field.</span>
            </div>
          </div>
          {status && <p className="status-inline">{status}</p>}
        </td>
      </tr>
    </tbody>
  );
};
